import { useState } from 'react'
import { API_BASE_URL } from '../config/api'

interface FormData {
  name: string
  email: string
  state: string
}

interface FormErrors {
  name?: string
  email?: string
  state?: string
}

const USA_STATES = [
  "Alabama", "Alaska", "Arizona", "Arkansas", "California", "Colorado", "Connecticut", "Delaware", "Florida", "Georgia", 
  "Hawaii", "Idaho", "Illinois", "Indiana", "Iowa", "Kansas", "Kentucky", "Louisiana", "Maine", "Maryland", 
  "Massachusetts", "Michigan", "Minnesota", "Mississippi", "Missouri", "Montana", "Nebraska", "Nevada", "New Hampshire", "New Jersey", 
  "New Mexico", "New York", "North Carolina", "North Dakota", "Ohio", "Oklahoma", "Oregon", "Pennsylvania", "Rhode Island", "South Carolina", 
  "South Dakota", "Tennessee", "Texas", "Utah", "Vermont", "Virginia", "Washington", "West Virginia", "Wisconsin", "Wyoming"
]

const Subscribe = () => {
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    state: ''
  })

  const [errors, setErrors] = useState<FormErrors>({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [showThankYou, setShowThankYou] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')

  // Validate email format
  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(email)
  }

  // Validate form inputs
  const validateForm = (): boolean => {
    const newErrors: FormErrors = {}

    // Name validation
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required'
    } else if (formData.name.trim().length < 2) {
      newErrors.name = 'Name must be at least 2 characters'
    }

    // Email validation
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required'
    } else if (!validateEmail(formData.email)) {
      newErrors.email = 'Please enter a valid email address'
    }

    // State validation
    if (!formData.state) {
      newErrors.state = 'Please select your state'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  // Handle input change
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
    
    // Clear error when user changes input
    if (errors[name as keyof FormErrors]) {
      setErrors(prev => ({
        ...prev,
        [name]: undefined
      }))
    }
    setErrorMessage('')
  }

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!validateForm()) {
      return
    }

    setIsSubmitting(true)
    setErrorMessage('')

    try {
      const response = await fetch(`${API_BASE_URL}newsletter_subscribe.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      })

      const result = await response.json()

      if (!response.ok || !result.status) {
        throw new Error(result.message || `Server error. Please try again.`)
      }

      // Show thank you popup
      setShowThankYou(true)
      
      // Reset form
      setFormData({
        name: '',
        email: '',
        state: ''
      })
      setErrors({})

    } catch (error: any) {
      console.error('Error subscribing:', error)
      setErrorMessage(error.message || 'Failed to subscribe. Please try again later.')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="min-h-screen bg-gray-900 flex flex-col justify-center relative overflow-hidden py-16 px-4">
      {/* Decorative gradient backgrounds */}
      <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary-light/10 rounded-full blur-3xl -z-10 animate-pulse"></div>
      <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-primary-dark/10 rounded-full blur-3xl -z-10 animate-pulse delay-700"></div>

      <div className="max-w-xl mx-auto w-full relative z-10">
        {/* Header content */}
        <div className="text-center mb-8">
          <span className="text-primary-light text-sm md:text-base font-semibold uppercase tracking-wider">
            Newsletter Subscription
          </span>
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mt-3 mb-4">
            Stay Connected
          </h1>
          <p className="text-gray-300 text-base md:text-lg">
            Join the LMT SEVA community to receive updates on upcoming live concerts, events, community service milestones, and more!
          </p>
        </div>

        {/* Subscribe Form Card */}
        <div className="bg-gray-800/80 backdrop-blur-md rounded-2xl shadow-2xl p-8 md:p-10 border border-gray-700">
          <form onSubmit={handleSubmit} className="space-y-6">
            
            {/* General API Error Alert */}
            {errorMessage && (
              <div className="bg-red-500/20 border border-red-500 text-red-200 px-4 py-3 rounded-lg text-sm flex items-center space-x-2">
                <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                <span>{errorMessage}</span>
              </div>
            )}

            {/* Name Field */}
            <div>
              <label htmlFor="name" className="block text-white font-semibold mb-2">
                Your Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className={`w-full px-4 py-3 rounded-lg bg-gray-700 text-white placeholder-gray-400 border-2 focus:outline-none focus:ring-2 transition-all ${
                  errors.name
                    ? 'border-red-500 focus:ring-red-500'
                    : 'border-gray-600 focus:border-primary-light focus:ring-primary-light/50'
                }`}
                placeholder="Enter your full name"
              />
              {errors.name && (
                <p className="mt-1 text-sm text-red-500">{errors.name}</p>
              )}
            </div>

            {/* Email Field */}
            <div>
              <label htmlFor="email" className="block text-white font-semibold mb-2">
                Your Email <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className={`w-full px-4 py-3 rounded-lg bg-gray-700 text-white placeholder-gray-400 border-2 focus:outline-none focus:ring-2 transition-all ${
                  errors.email
                    ? 'border-red-500 focus:ring-red-500'
                    : 'border-gray-600 focus:border-primary-light focus:ring-primary-light/50'
                }`}
                placeholder="Enter your email address"
              />
              {errors.email && (
                <p className="mt-1 text-sm text-red-500">{errors.email}</p>
              )}
            </div>

            {/* State Field */}
            <div>
              <label htmlFor="state" className="block text-white font-semibold mb-2">
                State (USA Only) <span className="text-red-500">*</span>
              </label>
              <select
                id="state"
                name="state"
                value={formData.state}
                onChange={handleChange}
                className={`w-full px-4 py-3 rounded-lg bg-gray-700 text-white border-2 focus:outline-none focus:ring-2 transition-all ${
                  errors.state
                    ? 'border-red-500 focus:ring-red-500'
                    : 'border-gray-600 focus:border-primary-light focus:ring-primary-light/50'
                }`}
              >
                <option value="">Select your state</option>
                {USA_STATES.map((state) => (
                  <option key={state} value={state}>
                    {state}
                  </option>
                ))}
              </select>
              {errors.state && (
                <p className="mt-1 text-sm text-red-500">{errors.state}</p>
              )}
            </div>

            {/* Submit Button */}
            <div>
              <button
                type="submit"
                disabled={isSubmitting}
                className={`w-full py-4 px-6 rounded-lg font-semibold text-white transition-all duration-300 ${
                  isSubmitting
                    ? 'bg-gray-600 cursor-not-allowed'
                    : 'bg-gradient-to-r from-primary-light to-primary-dark hover:from-primary-dark hover:to-primary-light shadow-lg hover:shadow-xl transform hover:-translate-y-1'
                }`}
              >
                {isSubmitting ? (
                  <span className="flex items-center justify-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Subscribing...
                  </span>
                ) : (
                  'Subscribe Now'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Thank You Popup Modal */}
      {showThankYou && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 backdrop-blur-sm">
          <div className="bg-gray-800 rounded-2xl shadow-2xl p-8 md:p-12 max-w-md mx-4 border border-gray-700 transform transition-all animate-fade-in text-center">
            {/* Success Checkmark Ring */}
            <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-500/20 border border-green-500 mb-6">
              <svg className="h-8 w-8 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            
            <h2 className="text-3xl font-bold text-white mb-4">Subscription Confirmed!</h2>
            <p className="text-gray-300 text-base md:text-lg mb-4">
              Thank you for subscribing to our newsletter!
            </p>
            <p className="text-gray-400 text-sm mb-6">
              You'll now receive timely updates about LMT SEVA's initiatives and community events.
            </p>

            <button
              onClick={() => setShowThankYou(false)}
              className="w-full py-3 bg-gradient-to-r from-primary-light to-primary-dark hover:from-primary-dark hover:to-primary-light text-white font-semibold rounded-lg transition-all duration-300 shadow-md transform hover:-translate-y-0.5"
            >
              Back to Subscribe
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

export default Subscribe
